import React, { useState } from 'react';
import { ArrowRight, ChevronDown, Info } from 'lucide-react';

/**
 * FAQ objections — planche 6 de la maquette « Refonte conversion ».
 *
 * Trois questions ouvertes par défaut, celles qui bloquent le plus : la légalité
 * de ne pas payer, ce que devient le courrier déposé, le risque d'être rappelé.
 * Bénéfice SEO en prime.
 *
 * ⚠️ La planche liste huit questions mais ne fournit la réponse que pour ces
 * trois-là ; les cinq autres y sont repliées et vides. Elles ne sont donc PAS
 * reprises ici : écrire du contenu juridique à leur place reviendrait à
 * l'inventer. Les intitulés manquants sont consignés dans QUESTIONS_SANS_REPONSE
 * ci-dessous, à compléter puis faire valider avant mise en ligne.
 */

// Intitulés présents dans la maquette, sans réponse rédigée. À traiter avant mise en ligne.
export const QUESTIONS_SANS_REPONSE = [
  'Combien de temps dure la prescription ?',
  'Que se passe-t-il si je ne réponds pas au courrier ?',
  'Un cabinet de recouvrement peut-il saisir mon compte bancaire ?',
  'Pourquoi le diagnostic est-il gratuit ?',
  'Êtes-vous des avocats ?',
];

const FAQS = [
  {
    q: 'Est-ce légal de ne pas payer une dette prescrite ?',
    a: (
      <>
        Oui — à condition de l'invoquer. Passé le délai de prescription, le créancier ne peut plus
        vous contraindre à payer par la voie judiciaire, mais le juge ne peut pas soulever la
        prescription de lui-même&nbsp;: c'est à vous de la faire valoir. La dette n'est pas effacée
        pour autant — si vous payez volontairement, vous ne pourrez pas récupérer la somme. D'où la
        règle&nbsp;:{' '}
        <strong className="font-semibold" style={{ color: '#1e293b' }}>
          ne versez jamais d'acompte «&nbsp;pour montrer votre bonne foi&nbsp;» avant d'avoir vérifié
        </strong>
        . Avant l'expiration du délai, un paiement partiel peut valoir reconnaissance de la dette et
        faire repartir le compteur&nbsp;; après, il peut valoir renonciation à une prescription
        pourtant acquise.
      </>
    ),
  },
  {
    q: 'Que devient le courrier que je dépose ?',
    a: (
      <>
        Vos documents servent uniquement à produire votre analyse. Ils sont conservés
        30&nbsp;jours, puis supprimés — et ne sont transmis à personne sans votre accord explicite.
        Vous pouvez demander leur suppression avant ce délai en répondant à l'e-mail qui accompagne
        votre rapport, ou par courrier à Sequana SASU, 4 rue du Général Roux, 77590
        Fontaine-le-Port.
      </>
    ),
  },
  {
    q: 'Vais-je être rappelé par un commercial ?',
    a: (
      <>
        Non, sauf si vous le demandez. Le rapport arrive par e-mail. Une case facultative, décochée
        par défaut, vous permet d'être mis en relation avec notre avocat partenaire&nbsp;: c'est le
        seul cas où vos coordonnées sont transmises, et vous pouvez revenir sur ce choix à tout
        moment.
      </>
    ),
  },
];

const FaqObjections = ({ onCTAClick }) => {
  // Les trois sont ouvertes par défaut : ce sont les objections bloquantes,
  // les replier reviendrait à les cacher.
  const [ouvertes, setOuvertes] = useState(() => FAQS.map((_, i) => i));
  const toggle = (i) =>
    setOuvertes((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  return (
    <section className="bg-white px-[18px] py-12 sm:px-6 sm:py-16 lg:px-[72px]">
      <div className="mx-auto max-w-[860px]">
        <div className="mb-8 text-center sm:mb-10">
          <h2 className="m-0 text-[26px] font-extrabold leading-tight tracking-tight sm:text-[34px]" style={{ color: '#1e3a8a' }}>
            Les questions que tout le monde se pose
          </h2>
          <p className="mx-auto mt-3 max-w-[620px] text-[15px] leading-relaxed sm:text-[17px]" style={{ color: '#475569', textWrap: 'pretty' }}>
            Recevoir une mise en demeure fait peur. Voici ce qu'il faut savoir avant de réagir.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {FAQS.map((f, i) => {
            const ouverte = ouvertes.includes(i);
            return (
              <div key={f.q} className="overflow-hidden rounded-xl border" style={{ borderColor: '#e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={ouverte}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  style={{ backgroundColor: ouverte ? '#f8fafc' : '#ffffff' }}
                >
                  <span className="text-[15px] font-semibold sm:text-base" style={{ color: '#1e293b' }}>
                    {f.q}
                  </span>
                  <ChevronDown
                    size={19}
                    className={`shrink-0 transition-transform ${ouverte ? 'rotate-180' : ''}`}
                    style={{ color: '#64748b' }}
                  />
                </button>
                {ouverte && (
                  <div className="border-t px-5 py-4 text-[14px] leading-relaxed sm:text-[15px]" style={{ borderColor: '#f1f5f9', color: '#475569' }}>
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA de clôture */}
        <div
          className="mt-8 flex flex-col items-center gap-5 rounded-2xl border px-6 py-8 text-center sm:mt-10 sm:flex-row sm:justify-between sm:text-left"
          style={{ borderColor: '#c7d2fe', backgroundColor: '#f5f3ff' }}
        >
          <div className="flex flex-col gap-1">
            <div className="text-[17px] font-bold sm:text-[19px]" style={{ color: '#312e81' }}>
              Votre courrier attend depuis combien de jours&nbsp;?
            </div>
            <div className="text-[14px] sm:text-[15px]" style={{ color: '#4338ca' }}>
              Trois minutes suffisent pour savoir si vous devez agir ou temporiser.
            </div>
          </div>
          <button
            type="button"
            onClick={onCTAClick}
            className="inline-flex shrink-0 items-center gap-[10px] rounded-full px-6 py-[14px] text-[15px] font-semibold text-white transition-transform active:scale-[0.99]"
            style={{ backgroundColor: '#1e3a8a', boxShadow: '0 10px 15px -3px rgba(30, 58, 138, 0.24)' }}
          >
            Analyser mes documents — gratuit
            <ArrowRight size={18} strokeWidth={2.2} />
          </button>
        </div>

        <div className="mt-5 flex items-center justify-center gap-2 text-[12.5px] sm:text-[13px]" style={{ color: '#64748b' }}>
          <Info size={15} className="shrink-0" />
          Information juridique générale. Ne remplace pas les conseils d'un avocat.
        </div>
      </div>
    </section>
  );
};

export default FaqObjections;
